import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  merge,
  skip,
  startWith,
  Subject,
} from 'rxjs';
import {
  PaywallIndicatorService,
  UpgradePlanEvaluationType,
} from 'src/app/services/paywall-indicator.service';
import { proFeatureSubscriptionTypes } from 'src/app/models/payment';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { ColorScheme } from '../standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';

export enum TopControlSize {
  XSmall = 'XSmall',
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large',
  XLarge = 'XLarge',
}

export enum TopToolbarTheme {
  DARK = 'dark',
  BLUE = 'blue',
}

export enum ExtendedTextboxType {
  SPACE = 'space',
  ROOM = 'room',
  BOARD = 'board',
  FOLDER = 'folder',
}

export enum MiddleSectionMenus {
  MainMenu,
  BoardSettingsMenu,
  ManageBoardsMenu,
  RenameTitlesMenu,
  RoomSettingsMenu,
  SpaceSettingsMenu,
  MoveToRoomMenu,
}

export enum MiddleSectionSubMenu {
  ManageAllBoards,
  BoardSettings,
  RoomSettings,
  RenameMenu,
  SpaceSettings,
  MoveToRoom,
}

export interface MiddleSectionMenuData {
  menu: MiddleSectionMenus;
  data?: any;
  colorScheme?: ColorScheme;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class WbTopControlsV2Service {
  private _topControlSize = new BehaviorSubject<TopControlSize>(
    this.calculateTopControlSize(document.documentElement.clientWidth),
  );
  public topControlSize$ = this._topControlSize.asObservable().pipe(distinctUntilChanged());

  private showSystemStatusButton = new BehaviorSubject<boolean>(false);
  public readonly showSystemStatusButton$ = this.showSystemStatusButton
    .asObservable()
    .pipe(distinctUntilChanged());

  private _enableTextbox = new Subject<ExtendedTextboxType | null>();
  public enableTextbox$ = this._enableTextbox
    .asObservable()
    .pipe(startWith(null), distinctUntilChanged());

  private _openMiddleSectionMenuBottomSheet = new Subject<MiddleSectionMenuData>();
  public openMiddleSectionMenuBottomSheet$ = this._openMiddleSectionMenuBottomSheet.asObservable();
  private _closeMiddleSectionMainMenu = new Subject<void>();
  public closeMiddleSectionMainMenu$ = merge(
    this._closeMiddleSectionMainMenu.asObservable(),
    this.sharedDataService.sessionView.current$.pipe(map(() => true)),
    this.spaceRepo.isCurrentUserHost$.pipe(
      skip(1),
      filter((isHost) => isHost === false),
    ),
  );

  public canUseProFeature$ = this.paywallIndicatorService.hasValidSubscription(
    proFeatureSubscriptionTypes,
    UpgradePlanEvaluationType.SPACE,
  );

  constructor(
    private paywallIndicatorService: PaywallIndicatorService,
    private sharedDataService: SessionSharedDataService,
    private spaceRepo: SpaceRepository,
  ) {}

  updateTopControlSize(width: number): void {
    const size = this.calculateTopControlSize(width);

    if (size !== this.topControlSizeValue) {
      this._topControlSize.next(size);
    }
  }

  calculateTopControlSize(width: number): TopControlSize {
    if (width >= 1200) {
      return TopControlSize.XLarge;
    }

    if (width >= 1000 && width < 1200) {
      return TopControlSize.Large;
    }

    if (width >= 800 && width < 1000) {
      return TopControlSize.Medium;
    }

    if (width >= 600 && width < 800) {
      return TopControlSize.Small;
    }

    return TopControlSize.XSmall;
  }

  public startEditingTitleByType(type: ExtendedTextboxType): void {
    this._enableTextbox.next(type);
  }

  public finishEditing(): void {
    this._enableTextbox.next(null);
  }

  closeMiddleSectionMainMenu(): void {
    this._closeMiddleSectionMainMenu.next();
  }

  toggleSystemStatusVisibility(value: boolean): void {
    this.showSystemStatusButton.next(value);
  }

  public get topControlSizeValue(): TopControlSize {
    return this._topControlSize.getValue();
  }

  public openMiddleSectionMenuBottomSheet(
    menu: MiddleSectionMenus = MiddleSectionMenus.MainMenu,
    data?: any,
    colorScheme?: ColorScheme,
  ): void {
    this._openMiddleSectionMenuBottomSheet.next({ menu, data, colorScheme });
  }
}
